import React from 'react';
import { SeatingTabClass } from 'Services/Api/_HumanWritten/SeatingService';
import ClassWizardPoints from './ClassWizardPoints';
import { describeClassPoints } from './seatingLayout';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';

export interface FareTypeOptionProps {
	passengerClass: SeatingTabClass;
	/** Whether this is the fare type the passenger is in. */
	chosen: boolean;
	/** The per-head fare for this passenger's ticket type, or null where none was resolved. */
	fare?: number | null;
	/** What moving here would add or save, against the fare type they are in now. */
	delta?: number | null;
	onChoose: () => void;
	/** Given only where there is a panel to open; without it no `i` marker is drawn. */
	onMoreInfo?: () => void;
	/**
	 * Whether to say what choosing a seat in this fare type costs.
	 *
	 * <p>Off at check-in, where there is no seat to choose: a staff member seating someone at the
	 * gate cannot act on a seat-selection fee, and printing one there offers something the screen
	 * does not do.</p>
	 */
	showSeatFee?: boolean;
	/**
	 * Whether to print what the fare type includes.
	 *
	 * <p>Off at check-in: the description sells the fare type to whoever is choosing it, and a staff
	 * member seating a booking has already been paid. Four lines of sales copy per passenger is the
	 * step's whole height for something nobody at the gate is reading.</p>
	 */
	showDescription?: boolean;
	/**
	 * Whether a full fare type can still be chosen. For managers, who can overbook a fare type the
	 * same way they can overbook a sailing — the row still says `Full`, so the choice is a known one.
	 */
	allowFull?: boolean;
}

/**
 * One fare type, as something to pick.
 *
 * <p>Shared by the wizard's seating step and the check-in add flow's fare-type step, because to
 * whoever is reading it they are the same control: a name, what it costs this passenger, how many
 * places are left, what it includes. The two screens differ in what they can do with a seat, which
 * is what `showSeatFee` and `onMoreInfo` decide — everything else is the same row.</p>
 */
export default function FareTypeOption({
	passengerClass,
	chosen,
	fare = null,
	delta = null,
	onChoose,
	onMoreInfo,
	showSeatFee = true,
	showDescription = true,
	allowFull = false,
}: FareTypeOptionProps) {
	// Unknown is not full: a trip the capacity engine cannot price sends no figure, and reading
	// that as zero closed every fare type on the step.
	const remaining = passengerClass.remaining ?? null;
	const full = remaining !== null && remaining <= 0;
	const placesLabel = (() => {
		if (full) {
			return 'Full';
		}
		// A fare type set not to show its count says only whether it has room, as the trip cards do.
		if (passengerClass.showCapacity === false) {
			return null;
		}
		return remaining !== null ? `${remaining} left` : null;
	})();
	const points = describeClassPoints(passengerClass.description);
	/*
		The difference in whole cents, decided before it is judged worth a chip.

		It is one fare subtracted from another, so it arrives with the noise that comes off a float:
		$49.99 − $23.99 printed as `+$26.000000000000004`, and two fare types that genuinely cost the
		same can differ by 4e-15 and earn a `+$0.00` chip saying nothing.
	*/
	const deltaCents = delta === null ? 0 : Math.round(delta * 100);

	return (
		<button
			type="button"
			disabled={full && !chosen && !allowFull}
			className={`seating-tab__option ${chosen ? 'seating-tab__option--on' : ''}`}
			onClick={onChoose}
		>
			<span className="seating-tab__option-head">
				{/*
					Everything the reader reads down the left, in one column, so the description starts
					under the name rather than under the money. The money stacks three lines deep and the
					name is one, so clearing the whole block left a hole the height of two lines between
					the fare type and what it includes.
				*/}
				<span className="seating-tab__option-main">
					<span className="seating-tab__option-name">
						{passengerClass.name}
						{onMoreInfo && (
							<span
								role="button"
								tabIndex={0}
								aria-label={`More about ${passengerClass.name}`}
								className="seating-tab__info"
								onClick={event => {
									// The row itself picks the fare type; opening the panel must not
									// also choose it.
									event.stopPropagation();
									onMoreInfo();
								}}
								onKeyDown={event => {
									if (event.key !== 'Enter' && event.key !== ' ') {
										return;
									}
									event.preventDefault();
									event.stopPropagation();
									onMoreInfo();
								}}
							>
								i
							</span>
						)}
						{/*
							What moving here would cost, not what it costs. The reader is already in a
							fare type; an absolute price makes them subtract.
						*/}
						{deltaCents !== 0 && (
							<span className="seating-tab__option-delta">
								{deltaCents > 0 ? '+' : '−'}
								{formatPriceDisplay(Math.abs(deltaCents) / 100, false)}
							</span>
						)}
					</span>

					{showDescription && points.length > 0 && (
						<span className="seating-tab__option-desc">{points.join(' · ')}</span>
					)}

					{showDescription && <ClassWizardPoints attributes={passengerClass.attributes} className="mt-1.5" />}

					{showSeatFee && passengerClass.allowsSeatSelection && (
						<span
							className={`seating-tab__option-seat ${
								passengerClass.seatSelectionFee
									? 'seating-tab__option-seat--paid'
									: 'seating-tab__option-seat--free'
							}`}
						>
							<i>{passengerClass.seatSelectionFee ? '$' : '✓'}</i>
							{passengerClass.seatSelectionFee
								? `Seat selection $${passengerClass.seatSelectionFee} per seat`
								: 'Free seat choice'}
						</span>
					)}
				</span>

				<span className="seating-tab__option-money">
					{fare !== null && (
						<>
							<span className="seating-tab__option-fare">${fare}</span>
							<span className="seating-tab__option-per">per person</span>
						</>
					)}
					{placesLabel && <span className="seating-tab__option-left">{placesLabel}</span>}
				</span>
			</span>
		</button>
	);
}
